import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { 
  FaCalendarAlt, FaFileExcel, FaFilePdf, FaFilter, FaChartBar, 
  FaSync, FaTimes, FaFileInvoice
} from "react-icons/fa";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import fetchData from "../../functions/fetchData";
import "./BillWiseSale.css";

export default function BillWiseSale() {
  const [billData, setBillData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString().split('T')[0];
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString().split('T')[0];
  
  const [filters, setFilters] = useState({
    startDate: startOfDay,
    endDate: endOfDay,
    billNo: "",
    minAmount: "",
    maxAmount: "",
    entertainmentMode: "all"
  });

  const fetchBillSales = async () => {
    try {
      setLoading(true);
      const response = await fetchData("final_bill", null, "id", {});
      const bills = Array.isArray(response) ? response : response?.data || [];
      // Exclude cancelled records
      const filteredBills = bills.filter(bill => bill.mode !== 'cancelled' && bill.mode !== 'Cancelled');
      setBillData(filteredBills);
      applyFilters();
    } catch (error) {
      console.error("Error fetching bill sales:", error);
      toast.error("Failed to fetch bill sales data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillSales();
  }, []);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [billData, filters]);

  const applyFilters = () => {
    let filtered = billData.filter(bill => {
      let billDate = null;
      if (bill.setup_dte) {
        try {
          const dateObj = new Date(bill.setup_dte);
          if (!isNaN(dateObj.getTime())) {
            billDate = dateObj.toISOString().split('T')[0];
          }
        } catch (e) {
          billDate = null;
        }
      }
      
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;
      const billDateObj = billDate ? new Date(billDate) : null;

      const dateMatch = 
        (!startDate || !billDateObj || billDateObj >= startDate) &&
        (!endDate || !billDateObj || billDateObj <= endDate);

      const billNoMatch = 
        !filters.billNo || 
        (bill.inv && bill.inv.toString().includes(filters.billNo));

      const amountMatch = 
        (!filters.minAmount || parseFloat(bill.net_amount || bill.total_amount || 0) >= parseFloat(filters.minAmount)) &&
        (!filters.maxAmount || parseFloat(bill.net_amount || bill.total_amount || 0) <= parseFloat(filters.maxAmount));

      let entertainmentMatch = true;
      if (filters.entertainmentMode === "only") {
        entertainmentMatch = bill.mode === "Entertainment";
      } else if (filters.entertainmentMode === "except") {
        entertainmentMatch = bill.mode !== "Entertainment";
      }

      return dateMatch && billNoMatch && amountMatch && entertainmentMatch;
    });

    setFilteredData(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      startDate: startOfDay,
      endDate: endOfDay,
      billNo: "",
      minAmount: "",
      maxAmount: "",
      entertainmentMode: "all"
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy');
    } catch {
      return dateStr;
    }
  };

  const calculateTotals = () => {
    return {
      totalBills: filteredData.length,
      barAmount: filteredData.reduce((sum, bill) => sum + (parseFloat(bill.bar_amount) || 0), 0),
      foodAmount: filteredData.reduce((sum, bill) => sum + (parseFloat(bill.food_amount) || 0), 0),
      shishaAmount: filteredData.reduce((sum, bill) => sum + (parseFloat(bill.shisha_amount) || 0), 0),
      discount: filteredData.reduce((sum, bill) => sum + (parseFloat(bill.discount) || 0), 0),
      netAmount: filteredData.reduce((sum, bill) => sum + (parseFloat(bill.net_amount) || parseFloat(bill.total_amount) || 0), 0)
    };
  };

  const totals = calculateTotals();

  const exportToCSV = () => {
    const csvData = filteredData.map(bill => ({
      'Bill No': bill.inv,
      'Date': formatDate(bill.dte),
      'Time': bill.tme || '-',
      'Table': bill.tables || '-',
      'Waiter': bill.waitor || '-',
      'Bar Amount': bill.bar_amount || 0,
      'Food Amount': bill.food_amount || 0,
      'Shisha Amount': bill.shisha_amount || 0,
      'Discount': bill.discount || 0,
      'Net Amount': bill.net_amount || bill.total_amount || 0,
      'Mode': bill.payment_mode || '-',
      'Zone': bill.zone || '-'
    }));

    return csvData;
  };

  const exportToPDF = () => {
    const doc = new jsPDF('portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 8;

    // Compact Header with light blue gradient
    doc.setFillColor(230, 240, 255);
    doc.rect(0, 0, pageWidth, 25, 'F');
    
    // Border for header
    doc.setDrawColor(180, 200, 230);
    doc.setLineWidth(0.5);
    doc.rect(0, 0, pageWidth, 25, 'S');
    
    // Report Title
    doc.setTextColor(40, 80, 150);
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('BILL WISE SALE REPORT', pageWidth / 2, 11, { align: 'center' });
    
    // Date Range and Generated timestamp in one line
    doc.setFontSize(7);
    doc.setTextColor(80, 80, 80);
    doc.setFont(undefined, 'normal');
    const dateRange = `Period: ${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`;
    doc.text(dateRange, pageWidth / 2, 18, { align: 'center' });
    
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, pageWidth / 2, 22, { align: 'center' });
    
    currentY = 30;

    // Summary Statistics Box with light background
    doc.setFillColor(250, 252, 255);
    doc.rect(10, currentY, pageWidth - 20, 28, 'F');
    doc.setDrawColor(200, 210, 230);
    doc.setLineWidth(0.3);
    doc.rect(10, currentY, pageWidth - 20, 28, 'S');
    
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    
    const leftX = 15;
    const rightX = pageWidth / 2 + 5;
    
    // Summary data
    doc.text(`Total Bills:`, leftX, currentY + 7);
    doc.setFont(undefined, 'normal');
    doc.text(`${totals.totalBills}`, leftX + 25, currentY + 7);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Bar Amount:`, rightX, currentY + 7);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.barAmount.toFixed(2)}`, rightX + 28, currentY + 7);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Food Amount:`, leftX, currentY + 14);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.foodAmount.toFixed(2)}`, leftX + 25, currentY + 14);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Shisha Amount:`, rightX, currentY + 14);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.shishaAmount.toFixed(2)}`, rightX + 28, currentY + 14);
    
    doc.setFont(undefined, 'bold');
    doc.text(`Discount:`, leftX, currentY + 21);
    doc.setFont(undefined, 'normal');
    doc.text(`THB ${totals.discount.toFixed(2)}`, leftX + 25, currentY + 21);
    
    doc.setFont(undefined, 'bold');
    doc.setTextColor(40, 80, 150);
    doc.text(`Net Total:`, rightX, currentY + 21);
    doc.setFontSize(9);
    doc.text(`THB ${totals.netAmount.toFixed(2)}`, rightX + 28, currentY + 21);
    
    currentY += 34;

    // Table with improved alignment
    const tableColumn = ["#", "Bill", "Date", "Table", "Waiter", "Bar", "Food", "Shisha", "Disc", "Net", "Pay"];
    const tableRows = filteredData.map((bill, index) => [
      (index + 1).toString(),
      bill.inv || '-',
      formatDate(bill.dte),
      bill.tables || '-',
      bill.waitor || '-',
      (bill.bar_amount?.toFixed(0) || '0'),
      (bill.food_amount?.toFixed(0) || '0'),
      (bill.shisha_amount?.toFixed(0) || '0'),
      (bill.discount?.toFixed(0) || '0'),
      ((bill.net_amount || bill.total_amount)?.toFixed(0) || '0'),
      (bill.payment_mode || bill.mode || '-')
    ]);

    // Add totals row
    tableRows.push([
      '',
      '',
      '',
      '',
      'TOTAL',
      totals.barAmount.toFixed(0),
      totals.foodAmount.toFixed(0),
      totals.shishaAmount.toFixed(0),
      totals.discount.toFixed(0),
      totals.netAmount.toFixed(0),
      ''
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY,
      theme: "grid",
      headStyles: {
        fillColor: [230, 240, 255],
        textColor: [40, 80, 150],
        fontSize: 7,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [180, 200, 230]
      },
      styles: {
        fontSize: 6.5,
        cellPadding: { top: 1.5, right: 1, bottom: 1.5, left: 1 },
        lineColor: [220, 230, 245],
        lineWidth: 0.1,
        textColor: [60, 60, 60]
      },
      alternateRowStyles: {
        fillColor: [248, 250, 255]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10, fontStyle: 'normal' },
        1: { halign: 'center', cellWidth: 18 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'center', cellWidth: 13 },
        4: { halign: 'center', cellWidth: 13 },
        5: { halign: 'right', cellWidth: 18 },
        6: { halign: 'right', cellWidth: 18 },
        7: { halign: 'right', cellWidth: 18 },
        8: { halign: 'right', cellWidth: 15 },
        9: { halign: 'right', cellWidth: 22, fontStyle: 'bold', textColor: [40, 80, 150] },
        10: { halign: 'center', cellWidth: 20, fontSize: 6 }
      },
      // Highlight totals row
      didParseCell: function(data) {
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [230, 240, 255];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [40, 80, 150];
        }
      },
      margin: { left: 10, right: 10 },
      tableWidth: pageWidth - 20
    });

    // Footer
    const finalY = doc.lastAutoTable.finalY || currentY;
    if (finalY < pageHeight - 15) {
      doc.setDrawColor(200, 210, 230);
      doc.setLineWidth(0.3);
      doc.line(10, pageHeight - 12, pageWidth - 10, pageHeight - 12);
      
      doc.setFontSize(7);
      doc.setTextColor(120, 120, 120);
      doc.text('This is a computer-generated report', pageWidth / 2, pageHeight - 8, { align: 'center' });
    }

    doc.save(`bill_wise_sale_${format(new Date(), "ddMMyyyy_HHmmss")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <Layout>
      <div className="bill-wise-sale-container">
        <Header title="📊 Bill Wise Sale Report" subtitle="Analyze your bills and sales performance" />

        {/* Filters Section */}
        <div className="bws-card mb-3">
          <div className="bws-card-header">
            <button 
              className="bws-btn bws-btn-primary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="me-2" /> {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            <button 
              className="bws-btn bws-btn-secondary"
              onClick={clearFilters}
              disabled={loading}
            >
              <FaTimes className="me-2" /> Clear Filters
            </button>
            <button 
              className="bws-btn bws-btn-refresh"
              onClick={fetchBillSales}
              disabled={loading}
            >
              <FaSync className={loading ? 'spinning' : ''} className="me-2" /> Refresh
            </button>
          </div>

          {showFilters && (
            <div className="bws-card-body">
              <div className="filter-row">
                <div className="filter-group">
                  <label>Start Date</label>
                  <input 
                    type="date" 
                    className="bws-input"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>End Date</label>
                  <input 
                    type="date" 
                    className="bws-input"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Bill No</label>
                  <input 
                    type="text" 
                    className="bws-input"
                    placeholder="Search bill number"
                    value={filters.billNo}
                    onChange={(e) => handleFilterChange('billNo', e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-row">
                <div className="filter-group">
                  <label>Min Amount</label>
                  <input 
                    type="number" 
                    className="bws-input"
                    placeholder="Min amount"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Max Amount</label>
                  <input 
                    type="number" 
                    className="bws-input"
                    placeholder="Max amount"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                  />
                </div>
                <div className="filter-group">
                  <label>Service Mode </label>
                  <select 
                    className="bws-input"
                    value={filters.entertainmentMode}
                    onChange={(e) => handleFilterChange('entertainmentMode', e.target.value)}
                  >
                    <option value="all">All Bills</option>
                    <option value="only">Only Entertainment</option>
                    <option value="except">Except Entertainment</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics Section */}
        <div className="bws-stats-container">
          <div className="bws-stat-card">
            <div className="stat-label">Total Bills</div>
            <div className="stat-value">{totals.totalBills}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Bar Amount</div>
            <div className="stat-value">{formatCurrency(totals.barAmount)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Food Amount</div>
            <div className="stat-value">{formatCurrency(totals.foodAmount)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Shisha Amount</div>
            <div className="stat-value">{formatCurrency(totals.shishaAmount)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Total Discount</div>
            <div className="stat-value">{formatCurrency(totals.discount)}</div>
          </div>
          <div className="bws-stat-card">
            <div className="stat-label">Net Amount</div>
            <div className="stat-value">{formatCurrency(totals.netAmount)}</div>
          </div>
        </div>

        {/* Export Buttons */}
        <div className="bws-card mb-3">
          <div className="bws-card-header">
            <button className="bws-btn bws-btn-success" onClick={exportToPDF}>
              <FaFilePdf className="me-2" /> Export PDF
            </button>
            <CSVLink 
              data={exportToCSV()} 
              filename={`bill_wise_sale_${format(new Date(), "ddMMyyyy_HHmmss")}.csv`}
              className="bws-btn bws-btn-info"
            >
              <FaFileExcel className="me-2" /> Export CSV
            </CSVLink>
          </div>
        </div>

        {/* Data Table */}
        <div className="bws-card">
          <div className="bws-card-body-zero">
            {loading ? (
              <div className="bws-loading">Loading bill data...</div>
            ) : filteredData.length === 0 ? (
              <div className="bws-empty-state">
                <FaFileInvoice className="empty-icon" />
                <p>No bills found for the selected filters</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="bws-table">
                    <thead>
                      <tr>
                        <th>Bill No</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Table</th>
                        <th>Waiter</th>
                        <th className="text-end">Bar</th>
                        <th className="text-end">Food</th>
                        <th className="text-end">Shisha</th>
                        <th className="text-end">Discount</th>
                        <th className="text-end">Net Amount</th>
                        <th>Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((bill, idx) => {
                        return (
                          <tr key={idx}>
                            <td><strong>{bill.inv || '-'}</strong></td>
                            <td>{formatDate(bill.dte)}</td>
                            <td>{bill.tme || '-'}</td>
                            <td>{bill.tables || '-'}</td>
                            <td>{bill.waitor || '-'}</td>
                            <td className="text-end amount-cell">{formatCurrency(bill.bar_amount)}</td>
                            <td className="text-end amount-cell">{formatCurrency(bill.food_amount)}</td>
                            <td className="text-end amount-cell">{formatCurrency(bill.shisha_amount)}</td>
                            <td className="text-end amount-cell">{formatCurrency(bill.discount)}</td>
                            <td className="text-end amount-cell">{formatCurrency(bill.net_amount || bill.total_amount)}</td>
                            <td>{bill.payment_mode || bill.mode || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5" className="footer-label">TOTAL</td>
                        <td className="text-end footer-value">{formatCurrency(totals.barAmount)}</td>
                        <td className="text-end footer-value">{formatCurrency(totals.foodAmount)}</td>
                        <td className="text-end footer-value">{formatCurrency(totals.shishaAmount)}</td>
                        <td className="text-end footer-value">{formatCurrency(totals.discount)}</td>
                        <td className="text-end footer-value">{formatCurrency(totals.netAmount)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bws-pagination">
                  <button 
                    className="bws-btn bws-btn-small"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages} (Showing {currentItems.length} of {filteredData.length})
                  </span>
                  <div className="pagination-select">
                    <label>Records per page:</label>
                    <select 
                      value={itemsPerPage} 
                      onChange={(e) => {
                        setItemsPerPage(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="bws-pagination-combo"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                  <button 
                    className="bws-btn bws-btn-small"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </Layout>
  );
}
